import { readableAccent, type ClassId } from "@eldrun/content";
import { GamepadNav, type PadAction } from "../../input/gamepad-nav.ts";
import { gameColor } from "../../render/style/roles.ts";
import { button } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import { tabKeyTarget } from "../kit/kit-model.ts";
import { classColumns, type ClassColumn, type PickSource, type Rect } from "./class-select-model.ts";

/** O que dá foco na navegação linear do controle (fora das colunas, que têm o foco itinerante). */
const FOCUSABLE = ":is(button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex='0']):not([tabindex='-1'])";

export interface ClassSelectOptions {
  /** `solo`: escolher já começa a partida; `lobby`: escolher troca a classe na sala (pronto é à parte). */
  mode: "solo" | "lobby";
  onChoose(id: ClassId): void;
  /** Voltar (Esc, B): ao menu no `solo`; no lobby, o foco vai ao "Sair" (sair é com confirmação). */
  onBack(): void;
  /** O destaque mudou (mouse ou foco): o palco 3D acende o modelo. */
  onHighlight?(id: ClassId | undefined): void;
  /** O palco de cada coluna na tela mudou: o palco 3D põe cada modelo sob a sua. */
  onLayout?(rects: Partial<Record<ClassId, Rect>>): void;
}

interface ColumnEls {
  button: HTMLButtonElement;
  stage: HTMLElement;
  picks: HTMLElement;
  key: string;
}

/**
 * Tela de escolha da classe (F85), a mesma no "Jogar sozinho" e no lobby: quatro colunas transparentes
 * sobre o palco 3D (`class-stage.ts`), cada uma com o modelo da classe atrás, a carta (forma, papel e as
 * três habilidades) e quem escolheu a classe (número, nome e pronto). Mouse, teclado (setas, Home/End,
 * Enter/Espaço, Esc) e controle (`GamepadNav`). As colunas nascem uma vez e só se atualizam no lugar: o
 * redesenho do lobby não tira o foco de quem navega pelo teclado ou pelo controle.
 */
export class ClassSelect {
  readonly root: HTMLElement;
  private readonly group: HTMLElement;
  private readonly columns = new Map<ClassId, ColumnEls>();
  private readonly order: ClassId[] = [];
  private readonly options: ClassSelectOptions;
  private readonly pad: GamepadNav;
  private readonly observer: ResizeObserver | undefined;
  /** A navegação do controle passa por aqui (no lobby, a tela inteira, com o painel da sala). */
  private navRoot: HTMLElement;
  private chosen: ClassId | undefined;
  private hovered: ClassId | undefined;
  private highlighted: ClassId | undefined;
  private locked = false;

  constructor(options: ClassSelectOptions) {
    this.options = options;
    const cols = classColumns(undefined);
    this.group = h("div", { class: "escolha-colunas", id: "classes", role: "radiogroup", "aria-label": "Classe" });
    for (const col of cols) {
      const els = this.column(col);
      this.columns.set(col.id, els);
      this.order.push(col.id);
      this.group.append(els.button);
    }
    const solo = options.mode === "solo";
    this.root = h(
      "section",
      { class: `escolha escolha--${options.mode}`, "aria-label": "Escolha da classe", "data-modo": options.mode },
      h(
        "header",
        { class: "escolha-cabeca" },
        h("h2", {}, "Escolha a classe"),
        h("p", { class: "hint" }, solo ? "Setas ou o direcional para olhar, Enter ou A para começar, Esc ou B para voltar." : "Setas ou o direcional para olhar, Enter ou A para escolher. Classes repetidas valem."),
        solo ? withId(button({ label: "Voltar", icon: "icone-voltar", onClick: () => this.back() }), "btn-voltar-classe") : null,
      ),
      this.group,
    );
    this.navRoot = this.root;
    this.root.addEventListener("keydown", (e) => this.onKey(e));
    this.group.addEventListener("pointerleave", () => this.hover(undefined));
    this.group.addEventListener("focusout", () => queueMicrotask(() => this.refreshHighlight()));
    this.pad = new GamepadNav((a) => this.onPad(a));
    // O palco de cada coluna muda de tamanho quando a carta cresce (quem escolheu entra embaixo): observa cada um.
    this.observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => this.reportLayout()) : undefined;
    this.observer?.observe(this.group);
    for (const els of this.columns.values()) this.observer?.observe(els.stage);
    this.setRoving(this.order[0]!);
  }

  /** Liga o controle e o foco (depois de entrar no documento). */
  start(navRoot: HTMLElement = this.root): void {
    this.navRoot = navRoot;
    this.pad.start();
    // Na tela estreita o lobby rola: os modelos seguem as colunas.
    document.addEventListener("scroll", this.onScroll, { capture: true, passive: true });
    if (!this.root.contains(document.activeElement)) this.focusColumn(this.chosen ?? this.order[0]!);
    this.reportLayout();
  }

  stop(): void {
    this.pad.stop();
    this.observer?.disconnect();
    document.removeEventListener("scroll", this.onScroll, { capture: true });
  }

  /** O foco na coluna da escolha (ou na primeira): o lobby usa quando o controle focado sumiu no redesenho. */
  focusChosen(): void {
    this.focusColumn(this.chosen ?? this.order[0]!);
  }

  private readonly onScroll = () => this.reportLayout();

  /** A minha escolha e as dos outros (no lobby, pelo estado da sala; sozinho, só a minha). */
  update(chosen: ClassId | undefined, players: readonly PickSource[] = []): void {
    this.chosen = chosen;
    for (const col of classColumns(chosen, players)) {
      const els = this.columns.get(col.id)!;
      els.button.setAttribute("aria-checked", String(col.active));
      els.button.classList.toggle("active", col.active);
      const key = JSON.stringify(col.picks);
      if (key !== els.key) {
        els.key = key;
        els.picks.replaceChildren(...col.picks.map((p) => this.chip(p, col)));
      }
    }
    // O foco itinerante segue a escolha quando o foco não está nas colunas.
    if (chosen && !this.group.contains(document.activeElement)) this.setRoving(chosen);
  }

  /** Trava a entrada (a escolha do "Jogar sozinho" já foi feita e a partida está abrindo). */
  lock(): void {
    this.locked = true;
    this.root.classList.add("escolha--travada");
  }

  /** Re-emite o destaque e o lugar das colunas (o palco 3D chegou depois da tela). */
  replay(): void {
    this.options.onHighlight?.(this.highlighted);
    this.reportLayout();
  }

  private column(col: ClassColumn): ColumnEls {
    const stage = h("span", { class: "escolha-palco", "aria-hidden": "true" });
    const picks = h("span", { class: "escolha-jogadores", "aria-live": "polite" });
    const el = h(
      "button",
      {
        type: "button",
        class: "escolha-coluna",
        role: "radio",
        "aria-checked": "false",
        "data-classe": col.id,
        title: col.skills.map((k) => `${k.kind} · ${k.name}: ${k.description}`).join("\n"),
        onclick: () => this.choose(col.id),
        // A última entrada manda: mexer o mouse sobre a coluna destaca ela; o foco (teclado, controle) troca o destaque.
        onpointermove: () => this.hovered !== col.id && this.hover(col.id),
        onfocus: () => this.hover(undefined),
      },
      stage,
      h(
        "span",
        { class: "escolha-carta" },
        h("span", { class: "escolha-topo" }, h("span", { class: "classe-glifo", "aria-hidden": "true" }, col.glyph), h("strong", {}, col.name)),
        h("span", { class: "papel" }, col.role),
        h("span", { class: "fantasia" }, col.fantasy),
        h("span", { class: "habilidades" }, ...col.skills.map((k) => h("span", { class: "classe-habilidade" }, h("span", { class: "classe-tipo" }, k.kind), h("span", { class: "classe-habilidade-nome" }, k.name)))),
        picks,
      ),
    );
    // A cor da classe pelo CSSOM (a CSP não aceita `style`); a clara é para o número no hexágono.
    const color = gameColor(col.color);
    el.style.setProperty("--cor", color);
    el.style.setProperty("--cor-claro", readableAccent(color));
    return { button: el, stage, picks, key: "" };
  }

  private chip(p: ClassColumn["picks"][number], col: ClassColumn): HTMLElement {
    const label = `Jogador ${p.n} · ${p.name}${p.isMe ? " (você)" : ""} · ${col.name}${p.ready ? " · pronto" : ""}`;
    return h(
      "span",
      { class: `escolha-chip${p.isMe ? " eu" : ""}${p.ready ? " pronto" : ""}`, "data-n": String(p.n), title: label, "aria-label": label },
      h("span", { class: "escolha-chip-n", "aria-hidden": "true" }, String(p.n)),
      h("span", { class: "escolha-chip-nome", "aria-hidden": "true" }, p.name),
      p.ready ? h("span", { class: "escolha-chip-pronto", "aria-hidden": "true" }, "✓") : null,
    );
  }

  private choose(id: ClassId): void {
    if (this.locked) return;
    this.setRoving(id);
    // Sozinho não há estado de sala: a escolha marca a coluna aqui (no lobby, vem do servidor).
    if (this.options.mode === "solo") this.update(id);
    this.options.onChoose(id);
  }

  private back(): void {
    if (!this.locked) this.options.onBack();
  }

  private hover(id: ClassId | undefined): void {
    this.hovered = id;
    this.refreshHighlight();
  }

  /** O destaque é o do mouse (enquanto ele se mexe sobre uma coluna); sem ele, o da coluna com o foco. */
  private refreshHighlight(): void {
    const focused = (document.activeElement as HTMLElement | null)?.dataset?.["classe"] as ClassId | undefined;
    const next = this.hovered ?? (focused && this.columns.has(focused) ? focused : undefined);
    if (next === this.highlighted) return;
    this.highlighted = next;
    for (const [id, els] of this.columns) els.button.classList.toggle("destaque", id === next);
    this.options.onHighlight?.(next);
  }

  private setRoving(id: ClassId): void {
    for (const [cid, els] of this.columns) els.button.tabIndex = cid === id ? 0 : -1;
  }

  private focusColumn(id: ClassId): void {
    this.setRoving(id);
    this.columns.get(id)?.button.focus();
  }

  private onKey(e: KeyboardEvent): void {
    const id = (e.target as HTMLElement).dataset?.["classe"] as ClassId | undefined;
    if (id && this.columns.has(id)) {
      const next = tabKeyTarget(e.key, this.order.indexOf(id), this.order.length);
      if (next !== undefined) {
        e.preventDefault();
        this.focusColumn(this.order[next]!);
        return;
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.back();
    }
  }

  /** O controle: nas colunas, esquerda e direita trocam de classe; o resto anda pela tela, em ordem. */
  private onPad(action: PadAction): void {
    if (this.locked) return;
    const active = document.activeElement as HTMLElement | null;
    if (!active || !this.navRoot.contains(active)) {
      if (action !== "back") return this.focusColumn(this.chosen ?? this.order[0]!);
    }
    if (action === "back") return this.back();
    if (action === "confirm") return active?.click();
    const inColumns = !!active && this.group.contains(active);
    if (inColumns && (action === "left" || action === "right")) {
      active!.dispatchEvent(new KeyboardEvent("keydown", { key: action === "left" ? "ArrowLeft" : "ArrowRight", bubbles: true }));
      return;
    }
    const all = [...this.navRoot.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === active);
    const index = active ? all.indexOf(active) : -1;
    const step = action === "left" || action === "up" ? -1 : 1;
    const target = all[index + step];
    if (target) target.focus();
  }

  private reportLayout(): void {
    if (!this.options.onLayout) return;
    const rects: Partial<Record<ClassId, Rect>> = {};
    for (const [id, els] of this.columns) {
      const r = els.stage.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) rects[id] = { x: r.left, y: r.top, w: r.width, h: r.height };
    }
    this.options.onLayout(rects);
  }
}

function withId<T extends HTMLElement>(el: T, id: string): T {
  el.id = id;
  return el;
}
